// escpos.ts — raster ESC/POS (GS v 0, una sola pasada de imagen)

import QRCode from 'qrcode';

const PRINT_PX  = 384;
const MARGIN_X  = 10;
const USABLE_PX = PRINT_PX - MARGIN_X * 2;

const F_NORM = 28;
const F_BIG  = 44;
const F_HUGE = 58;

const LH_NORM = 34;
const LH_BIG  = 52;
const LH_HUGE = 68;

const LINE_WIDTH = 22;

export interface TicketActa {
  acta_nro:                    string;
  patente:                     string;
  tipo_codigo:                 string | null;
  descripcion_snapshot:        string | null;
  articulo_snapshot:           string | null;
  monto_centavos:              number;
  monto_pronto_pago_centavos:  number | null;
  vence_pronto_pago_en:        string | null;
  direccion:                   string | null;
  creado_en:                   string;
  inspector_nombre:            string;
  inspector_legajo:            string | null;
  pagar_url:                   string;
}

type DrawLine = { text: string; font: string; lh: number; center?: boolean };

// ── Helpers ───────────────────────────────────────────────────────────────────

function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\x00-\xff]/g, '?');
}

function wordWrap(text: string, width: number): string[] {
  const words = fold(text).split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    if (!cur) { cur = w.slice(0, width); continue; }
    if (cur.length + 1 + w.length <= width) cur += ' ' + w;
    else { lines.push(cur); cur = w.slice(0, width); }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

function pesos(cents: number): string {
  return '$' + (cents / 100).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtFechaHora(s: string): string {
  const d = new Date(s.replace(' ', 'T'));
  return (
    d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  );
}

function fmtFecha(s: string): string {
  return new Date(s.replace(' ', 'T')).toLocaleDateString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function makeFont(size: number, bold = false): string {
  return `${bold ? 'bold ' : ''}${size}px Arial, Helvetica, sans-serif`;
}

// ── Layout ────────────────────────────────────────────────────────────────────

function buildLines(t: TicketActa): DrawLine[] {
  const lines: DrawLine[] = [];
  const add = (text: string, size = F_NORM, bold = false, center = false) =>
    lines.push({ text: fold(text), font: makeFont(size, bold),
                 lh: size >= F_HUGE ? LH_HUGE : size >= F_BIG ? LH_BIG : LH_NORM, center });
  const sep = (c = '=') => add(c.repeat(LINE_WIDTH));

  add('INFRACCION',  F_BIG, true, true);
  add('DE TRANSITO', F_BIG, true, true);
  sep();
  add(fold(t.acta_nro), F_NORM, false, true);
  add(fmtFechaHora(t.creado_en), F_NORM, false, true);
  add(t.patente, F_HUGE, true, true);
  if (t.tipo_codigo)       add(fold(t.tipo_codigo), F_NORM, true, true);
  if (t.articulo_snapshot) add(`Art. ${fold(t.articulo_snapshot)}`, F_NORM, false, true);
  if (t.direccion) wordWrap(t.direccion, LINE_WIDTH).forEach(l => add(l));
  if (t.inspector_legajo)  add(`Legajo: ${fold(t.inspector_legajo)}`, F_NORM, false, false);

  const ppVigente =
    t.monto_pronto_pago_centavos !== null &&
    t.vence_pronto_pago_en       !== null &&
    new Date(t.vence_pronto_pago_en.replace(' ', 'T')) > new Date();

  sep();
  if (ppVigente) {
    add(pesos(t.monto_pronto_pago_centavos!), F_HUGE, true, true);
    add(`PP hasta ${fmtFecha(t.vence_pronto_pago_en!)}`, F_NORM, false, true);
    add(`Completo: ${pesos(t.monto_centavos)}`, F_NORM, false, true);
  } else {
    add(pesos(t.monto_centavos), F_HUGE, true, true);
  }
  sep();
  return lines;
}

// ── QR ───────────────────────────────────────────────────────────────────────

async function loadQR(url: string, sizePx: number): Promise<HTMLImageElement | null> {
  try {
    const dataUrl = await QRCode.toDataURL(url, {
      width: sizePx, margin: 1, errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#ffffff' },
    });
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload  = () => resolve(img);
      img.onerror = reject;
      img.src     = dataUrl;
    });
  } catch {
    return null;
  }
}

// ── Canvas → bitmap 1-bit ─────────────────────────────────────────────────────

function canvasToBitmap(ctx: CanvasRenderingContext2D, width: number, height: number): Uint8Array {
  const imgData    = ctx.getImageData(0, 0, width, height);
  const widthBytes = Math.ceil(width / 8);
  const bitmap     = new Uint8Array(widthBytes * height);
  for (let row = 0; row < height; row++) {
    for (let col8 = 0; col8 < widthBytes; col8++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const px = col8 * 8 + bit;
        if (px < width) {
          const i    = (row * width + px) * 4;
          const gray = (imgData.data[i] * 299 + imgData.data[i+1] * 587 + imgData.data[i+2] * 114) / 1000;
          if (gray < 128) byte |= (0x80 >> bit);
        }
      }
      bitmap[row * widthBytes + col8] = byte;
    }
  }
  return bitmap;
}

// ── buildTicket ───────────────────────────────────────────────────────────────

export async function buildTicket(t: TicketActa): Promise<Uint8Array> {
  const QR_PX = 200;

  // ── Render canvas ──────────────────────────────────────────────────────────
  const lines = buildLines(t);
  let totalH  = 24;
  for (const l of lines) totalH += l.lh;
  totalH += 20 + QR_PX + 160; // espacio antes del QR + QR + margen corte

  const canvas = document.createElement('canvas');
  canvas.width  = PRINT_PX;
  canvas.height = totalH;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, PRINT_PX, totalH);
  ctx.fillStyle = '#000000';

  let y = 24;
  for (const l of lines) {
    y += l.lh;
    ctx.font = l.font;
    if (l.center) {
      const w = ctx.measureText(l.text).width;
      ctx.fillText(l.text, Math.max(MARGIN_X, (PRINT_PX - w) / 2), y);
    } else {
      ctx.fillText(l.text, MARGIN_X, y, USABLE_PX);
    }
  }

  y += 20;
  const qrImg = await loadQR(t.pagar_url, QR_PX);
  if (qrImg) ctx.drawImage(qrImg, (PRINT_PX - QR_PX) / 2, y, QR_PX, QR_PX);

  // ── Bitmap ─────────────────────────────────────────────────────────────────
  const bitmap     = canvasToBitmap(ctx, PRINT_PX, totalH);
  const widthBytes = Math.ceil(PRINT_PX / 8); // 48

  // Tiras de 24 filas: probado funcional con este modelo de impresora.
  // Más filas por tira = menos comandos = menos pauses, pero riesgo de overflow.
  const STRIP_H = 24;
  const out: number[] = [];
  out.push(0x1b, 0x40); // ESC @ init

  for (let row = 0; row < totalH; row += STRIP_H) {
    const rows  = Math.min(STRIP_H, totalH - row);
    out.push(
      0x1d, 0x76, 0x30, 0x00,
      widthBytes & 0xff, (widthBytes >> 8) & 0xff,
      rows       & 0xff, (rows       >> 8) & 0xff,
    );
    const strip = bitmap.slice(row * widthBytes, (row + rows) * widthBytes);
    for (let i = 0; i < strip.length; i++) out.push(strip[i]);
  }

  out.push(0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a); // feed — cortar cómodo

  return new Uint8Array(out);
}
